import { Component, inject, type OnInit, signal } from "@angular/core";
import { RouterLink, RouterLinkActive } from "@angular/router";
import { FontAwesomeModule } from "@fortawesome/angular-fontawesome";
import { faBars } from "@fortawesome/free-solid-svg-icons";
import { TranslatePipe } from "@ngx-translate/core";
import { filter } from "rxjs";

import { PermissionActionDescriptorType } from "../../../../../adaa-types";
import { TranslateTokenPipe } from "../../../../core/pipes";
import { AdaaHelper } from "../../../../core/utils";
import { Constants } from "../../../constants/constants";
import { PermissionAction } from "../../../constants/enums";
import { HasPermissionDirective } from "../../../directives";
import { ParameterCatalog, PermissionActionModelType } from "../../../models";
import {
  ConsoleService,
  GovScoresApiService,
  LanguageService,
  SystemLayoutService,
  UsersApiService,
} from "../../../services";
import { ColorLegendComponent } from "../../charts/color-legend/color-legend";
import { FloatActionComponent } from "../../general";
import { SpinnerComponent } from "../../spinner/spinner";
import { BreadcrumbComponent } from "./breadcrumb/breadcrumb";
import { TopbarComponent } from "./topbar/topbar";

@Component({
  selector: "adaa-system-layout",
  templateUrl: "./system-layout.html",
  styleUrl: "./system-layout.scss",
  imports: [
    TranslatePipe,
    RouterLink,
    RouterLinkActive,
    FontAwesomeModule,
    SpinnerComponent,
    TopbarComponent,
    BreadcrumbComponent,
    TranslateTokenPipe,
    HasPermissionDirective,
    ColorLegendComponent,
    FloatActionComponent,
  ],
})
export class SystemLayoutComponent implements OnInit {
  private readonly _usersApiService = inject(UsersApiService);
  private readonly _govScoresApiService = inject(GovScoresApiService);
  readonly languageService = inject(LanguageService);
  readonly systemLayoutService = inject(SystemLayoutService);
  readonly consoleService = inject(ConsoleService);

  sidebarType = signal<"full" | "mini">("full");

  readonly currentYear = () => new Date().getFullYear();

  readonly #untilDestroy = AdaaHelper.untilDestroyed();

  private get _sessionId(): string {
    const sessionId = AdaaHelper.getLocalStorage(Constants.localStorageKeys.sessionId, { type: "string" });
    return `${sessionId}`;
  }

  private get _userEntityId(): number {
    const userEntityId = AdaaHelper.getLocalStorage("user", { type: "prop", property: "entityId" });
    return +userEntityId;
  }

  public get icon() {
    return {
      menu: faBars,
    };
  }

  public ngOnInit() {
    this._hideRecaptchaBadge();
    this.systemLayoutService.init();

    if (!AdaaHelper.isDesktopDevice()) this.sidebarType.set("mini");

    this.systemLayoutService.hasActiveEntityChanged$.pipe(this.#untilDestroy()).subscribe(() => {
      this.consoleService.init(false);
    });

    this.systemLayoutService.hasCycleChanged$.pipe(this.#untilDestroy()).subscribe(() => {
      this.consoleService.init(false);
    });
  }

  public toggleSidebar() {
    this.sidebarType.update((t) => (t === "full" ? "mini" : "full"));
  }

  public sideNavClick(event: MouseEvent) {
    if (event.ctrlKey || event.metaKey || event.button === 1) return;
    if (this.sidebarType() === "full" && !AdaaHelper.isDesktopDevice()) this.toggleSidebar();
  }

  public getPermission(permissionId: string | undefined): PermissionActionDescriptorType | undefined {
    if (permissionId) {
      const permissions = permissionId.split(",");
      const permissionsArray: PermissionActionModelType[] = [];

      permissions.forEach((e) => {
        permissionsArray.push(
          {
            permissionAction: PermissionAction.view,
            objectTypeId: +e,
          },
          {
            permissionAction: PermissionAction.manage,
            objectTypeId: +e,
          },
          {
            permissionAction: PermissionAction.execute,
            objectTypeId: +e,
          }
        );
      });

      return {
        actor: {
          modifier: "or",
          permissions: permissionsArray,
        },
      };
    } else return undefined;
  }

  public openAdaaDashboard(route: ParameterCatalog) {
    if (!route.urlRoute) return;

    if (route.fieldName === "governmentPerformanceDashboard" && this._userEntityId !== Constants.CONSTANT_PMO_ID) {
      this._govScoresApiService.sectorAndStrategicPillarByEntity(this._userEntityId).subscribe({
        next: (res) => {
          if (res.inError) return;

          const url = `${route.urlRoute}/sector/${res.responseData.sectorId}/entity/${this._userEntityId}/pillar/${res.responseData.strategicPillarId}`;
          this._navigateToAdaaDashboard(url, { year: "2025" });
        },
      });
    } else this._navigateToAdaaDashboard(route.urlRoute);
  }

  private _hideRecaptchaBadge() {
    const recaptchaBadge = document.querySelector(".grecaptcha-badge");
    if (!recaptchaBadge) return;

    recaptchaBadge.setAttribute("style", "visibility: hidden; display: none;");
  }

  private _navigateToAdaaDashboard(route: string, params: Record<string, string> = {}) {
    this._usersApiService
      .getHashedSessionId(this._sessionId)
      .pipe(filter((res) => !res.inError))
      .subscribe({
        next: (res) => {
          const url = new URL(route, window.location.origin);
          const queryParams = {
            ...params,
            entityId: `${this._userEntityId}`,
            sessionId: res.responseData,
          };

          Object.entries(queryParams).forEach(([key, value]) => url.searchParams.set(key, value));

          window.open(url.toString(), "_blank");
        },
      });
  }
}
